'use client';

import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import useMarketsPriceHistory from '@/hooks/use-markets-price-history';
import { isTimePoint } from '@/lib/utils/date';
import MarketTrendChart from './MarketTrendChart';
import MarketOutcomes from './MarketOutcomes';
import { getMarketKey } from './marketKey';
import type { EventData, PolymarketMarket } from './types';

type MarketTrendRange = '1D' | '7D' | '1M' | 'ALL';
const MARKET_TREND_RANGES: MarketTrendRange[] = ['1D', '7D', '1M', 'ALL'];

interface MarketChartProps {
  eventData: EventData;
  markets: PolymarketMarket[];
  openInSidebar?: boolean;
}

export default function MarketChart({ eventData, markets, openInSidebar }: MarketChartProps) {
  const [selectedMarketIds, setSelectedMarketIds] = useState<Set<string>>(() =>
    getInitialSelectedMarketIds(markets),
  );

  useEffect(() => {
    setSelectedMarketIds((current) => {
      const availableMarketKeys = new Set(markets.map(getMarketKey).filter(Boolean));
      const preservedKeys = [...current].filter((key) => availableMarketKeys.has(key));

      if (preservedKeys.length > 0) return new Set(preservedKeys);

      return getInitialSelectedMarketIds(markets);
    });
  }, [markets]);

  const selectedMarkets = useMemo(() => {
    if (selectedMarketIds.size === 0) return [];

    return markets.filter((market) => selectedMarketIds.has(getMarketKey(market)));
  }, [markets, selectedMarketIds]);

  const handleMarketToggle = (market: PolymarketMarket) => {
    const marketKey = getMarketKey(market);
    if (!marketKey) return;

    setSelectedMarketIds((current) => {
      const next = new Set(current);
      if (next.has(marketKey)) {
        if (next.size <= 1) return current;
        next.delete(marketKey);
      } else {
        next.add(marketKey);
      }
      return next;
    });
  };

  return (
    <>
      <NormalMarketChart
        eventData={eventData}
        selectedMarkets={selectedMarkets}
        openInSidebar={openInSidebar}
      />
      <MarketOutcomes
        eventSlug={eventData.event.slug}
        markets={markets}
        selectedMarketIds={selectedMarketIds}
        onMarketToggle={handleMarketToggle}
      />
    </>
  );
}

function NormalMarketChart({
  eventData,
  selectedMarkets,
  openInSidebar,
}: {
  eventData: EventData;
  selectedMarkets: PolymarketMarket[];
  openInSidebar?: boolean;
}) {
  const { t } = useTranslation();
  const isClosed = eventData.event.closed === 1;
  const [timeRange, setTimeRange] = useState<MarketTrendRange>('ALL');

  const isTimePointChart = useMemo(
    () => selectedMarkets.every((market) => isTimePoint(market.group_item_title)),
    [selectedMarkets],
  );

  const historyWindow = useMemo(() => {
    const startTimes = selectedMarkets
      .map((market) => dayjs(market.created_at || market.start_date_iso).valueOf())
      .filter(Number.isFinite);
    const allStartTime =
      startTimes.length > 0 ? dayjs(Math.min(...startTimes)).toISOString() : null;
    const endTime = isClosed ? resolveClosedMarketsEndTime(selectedMarkets) : dayjs().toISOString();

    return {
      startTime: resolveRangeStartTime(endTime, allStartTime, timeRange),
      endTime,
    };
  }, [isClosed, selectedMarkets, timeRange]);

  const historyRequests = useMemo(
    () =>
      selectedMarkets.map((market) => ({
        conditionId: market.condition_id,
        market: market.clob_tokens_id_yes,
      })),
    [selectedMarkets],
  );

  const { data: historiesData, isLoading: isLoadingHistories } = useMarketsPriceHistory({
    markets: historyRequests,
    startTime: historyWindow.startTime,
    endTime: historyWindow.endTime,
    swr: {
      keepPreviousData: true,
    },
  });

  return (
    <section className="mt-5 relative">
      <div className="absolute z-10 right-0 flex h-10 w-fit items-center gap-1 rounded-lg bg-[#121212] p-1">
        {MARKET_TREND_RANGES.map((range) => (
          <button
            key={range}
            type="button"
            className={cn(
              'h-7 min-w-10 rounded-md px-2 text-xs font-medium text-[#B7B9BC] transition-colors hover:text-white',
              {
                'bg-[#262626] text-white': timeRange === range,
              },
            )}
            onClick={() => setTimeRange(range)}
          >
            {t(range)}
          </button>
        ))}
      </div>
      <MarketTrendChart
        markets={selectedMarkets}
        isTimePointChart={isTimePointChart}
        openInSidebar={openInSidebar}
        historiesData={historiesData}
        isLoadingHistories={isLoadingHistories}
      />
    </section>
  );
}

function getInitialSelectedMarketIds(markets: PolymarketMarket[]) {
  return new Set(markets.slice(0, 4).map(getMarketKey).filter(Boolean));
}

function resolveClosedMarketsEndTime(markets: PolymarketMarket[]) {
  const endTimes = markets
    .map((market) => dayjs(market.closed_time ?? market.end_date).valueOf())
    .filter(Number.isFinite);

  return endTimes.length > 0 ? dayjs(Math.max(...endTimes)).toISOString() : dayjs().toISOString();
}

function resolveRangeStartTime(
  endTime: string | null,
  allStartTime: string | null,
  range: MarketTrendRange,
) {
  if (range === 'ALL') return allStartTime;

  const endDate = dayjs(endTime);
  if (!endDate.isValid()) return allStartTime;

  switch (range) {
    case '1D':
      return endDate.subtract(1, 'day').toISOString();
    case '7D':
      return endDate.subtract(7, 'day').toISOString();
    case '1M':
      return endDate.subtract(1, 'month').toISOString();
  }
}
